import { gql, useMutation } from '@apollo/client';
import { Button } from 'components/button';
import { Dropdown } from 'components/dropdown-v7';
import { Input } from 'components/input';
import { Modal } from 'components/modal';
import { Tag } from 'components/tag';
import { format, formatISO } from 'date-fns';
import type {
  CustomerDoseState,
  CustomerMedicationFragment,
  DoseUnit,
  UpdateCustomerDoseInput,
  UpdateCustomerDoseMutation,
  UpdateCustomerDoseMutationVariables,
} from 'graphql/types';
import { useNotifications } from 'notifications';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type Props = { data: CustomerMedicationFragment };

export function Medication(props: Props) {
  const [updateDoseModalId, setUpdateDoseModalId] = useState<string>('');
  const selectedDose = props.data.dosageSchedules
    ?.flatMap((s) => s.doses ?? [])
    .find((d) => d.id === updateDoseModalId);
  return (
    <div className="flex flex-col space-y-4">
      {props.data.dosageSchedules?.map((dosageSchedule) => (
        <div
          key={dosageSchedule.id}
          className="bg-white shadow border border-slate-200 rounded-md flex flex-col flex-1 overflow-hidden text-slate-700"
        >
          <div className="w-full flex space-x-4 items-center bg-slate-50 px-5 py-4 border-b border-slate-200">
            <h1 className="font-semibold select-none">
              {dosageSchedule.schedule
                ? dosageSchedule.schedule.internalName
                : 'Dose Schedule'}
            </h1>
            {(() => {
              switch (dosageSchedule.state) {
                case 'ACTIVE':
                  return (
                    <Tag size="medium" color="green">
                      Active
                    </Tag>
                  );
                case 'CANCELED':
                  return (
                    <Tag size="medium" color="red">
                      Cancelled
                    </Tag>
                  );
                case 'PAUSED':
                  return (
                    <Tag size="medium" color="blue">
                      Paused
                    </Tag>
                  );
                default:
                  return <></>;
              }
            })()}
          </div>
          <table className="table-fixed text-left w-full">
            <thead className="text-xs leading-8 text-slate-500 font-normal bg-white">
              <tr>
                <th className="pl-4 w-1/6">Status</th>
                <th className="pl-4 w-1/6">Date Due</th>
                <th className="pl-4 w-1/6">Date Taken</th>
                <th className="pl-4 w-1/6">Dose Amount</th>
                <th className="pl-4 w-1/6">Taken Amount</th>
                <th className="w-1/6" />
              </tr>
            </thead>
            <tbody className="text-xs text-slate-700 leading-8 [&>*:nth-child(2n)]:bg-white [&>*:nth-child(2n+1)]:bg-slate-50">
              {dosageSchedule.doses?.map((d) => {
                const actualQuantity = d.actualQuantity?.toFixed(2);

                return (
                  <tr key={d.id}>
                    <td className="pl-4">
                      {(() => {
                        switch (d.state) {
                          case 'TAKEN':
                            return (
                              <Tag size="small" color="green">
                                Taken
                              </Tag>
                            );
                          case 'MISSED':
                            return (
                              <Tag size="small" color="red">
                                Missed
                              </Tag>
                            );
                          case 'UNKNOWN':
                            return (
                              <Tag size="small" color="gray">
                                No details
                              </Tag>
                            );
                          default:
                            return <></>;
                        }
                      })()}
                    </td>
                    <td className="pl-4">
                      {format(new Date(d.dueAt), 'dd-MM-yyyy')}
                    </td>
                    <td className="pl-4">
                      {d.dateTaken
                        ? format(new Date(d.dateTaken), 'dd-MM-yyyy')
                        : 'N/A'}
                    </td>
                    <td className="pl-4">
                      {d.scheduleDose?.dose?.quantity &&
                      d.scheduleDose?.dose?.unit
                        ? `${d.scheduleDose.dose.quantity} ${formatDoseUnit(
                            d.scheduleDose.dose.unit,
                          )}`
                        : ''}
                    </td>
                    <td className="pl-4">
                      {actualQuantity && d.actualUnit
                        ? `${actualQuantity} ${formatDoseUnit(d.actualUnit)}`
                        : 'N/A'}
                    </td>
                    <td className="pl-4">
                      <Button
                        onClick={() => setUpdateDoseModalId(d.id)}
                        variant="outline"
                        size="small"
                      >
                        Update
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {selectedDose && (
        <Modal
          show={setUpdateDoseModalId !== null}
          onClose={() => setUpdateDoseModalId('')}
          isAutoOverflow={false}
        >
          <UpdateDoseModal
            dose={selectedDose}
            onClose={() => setUpdateDoseModalId('')}
          />
        </Modal>
      )}
    </div>
  );
}
type UpdateDoseModalFormData = {
  id: string;
  state: CustomerDoseState;
  quantity?: number;
  unit?: DoseUnit;
  dateTaken?: string;
};

function UpdateDoseModal(props: {
  dose: {
    id: string;
    state: CustomerDoseState;
    actualQuantity?: number | null | undefined;
    actualUnit?: DoseUnit | null | undefined;
    dateTaken?: string | null | undefined;
  };
  onClose: () => void;
}) {
  const form = useForm<UpdateDoseModalFormData>({
    mode: 'onChange',
    defaultValues: {
      id: props.dose.id,
      state: props.dose.state,
      quantity: Number(props.dose.actualQuantity?.toFixed(2)) ?? undefined,
      unit: props.dose.actualUnit ?? undefined,
      dateTaken: props.dose.dateTaken
        ? formatISO(new Date(props.dose.dateTaken), { representation: 'date' })
        : undefined,
    },
  });
  const [updateCustomerDose] = useMutation<
    UpdateCustomerDoseMutation,
    UpdateCustomerDoseMutationVariables
  >(gql`
    mutation UpdateCustomerDose($input: UpdateCustomerDoseInput!) {
      updateCustomerDose(input: $input) {
        customerDose {
          id
          state
          dateTaken
          actualQuantity
          actualUnit
        }
      }
    }
  `);
  const notify = useNotifications();

  const watchedState = form.watch('state');
  if (watchedState !== 'TAKEN') {
    form.setValue(
      'quantity',
      Number(props.dose.actualQuantity?.toFixed(2)) ?? undefined,
    );
    form.setValue('unit', props.dose.actualUnit ?? undefined);
    form.setValue(
      'dateTaken',
      props.dose.dateTaken
        ? formatISO(new Date(props.dose.dateTaken), { representation: 'date' })
        : undefined,
    );
  }

  return (
    <form
      className="bg-white rounded border-slate-200 h-2/5"
      onSubmit={form.handleSubmit(async (formValues) => {
        let input: UpdateCustomerDoseInput;
        switch (formValues.state) {
          case 'MISSED':
            input = {
              missed: {
                id: formValues.id,
              },
            };
            break;
          case 'TAKEN':
            if (!formValues.dateTaken) {
              notify({
                message: 'Date Taken is required when updating state to taken',
                type: 'error',
              });
              return;
            }
            if (!formValues.quantity) {
              notify({
                message: 'Quantity is required when updating state to taken',
                type: 'error',
              });
              return;
            }
            if (!formValues.unit) {
              notify({
                message: 'Unit is required when updating state to taken',
                type: 'error',
              });
              return;
            }
            input = {
              taken: {
                id: formValues.id,
                takenDate: formValues.dateTaken,
                takenQuantity: formValues.quantity,
                takenUnit: formValues.unit,
              },
            };
            break;
          case 'UNKNOWN':
            input = {
              unknown: {
                id: formValues.id,
              },
            };
            break;
          default:
            throw Error('unexpected dose state');
        }
        await updateCustomerDose({ variables: { input } });
        props.onClose();
      })}
    >
      <div className="bg-slate-50 px-5 py-4 border-b border-slate-200">
        <h1 className="font-semibold select-none">Update Customer Dose</h1>
      </div>
      <div className="px-5 py-4">
        <div className="flex flex-col gap-4">
          <Dropdown
            label="State"
            name="state"
            placeholder="Dose State"
            control={form.control}
            options={[
              { value: 'TAKEN', label: 'Dose Taken' },
              { value: 'MISSED', label: 'Dose Missed' },
              { value: 'UNKNOWN', label: 'No details' },
            ]}
          />
          <div className={`w-2/5 ${watchedState === 'TAKEN' ? '' : 'hidden'}`}>
            <Input
              type="date"
              label="Date Taken"
              placeholder="Date taken"
              {...form.register('dateTaken')}
            />
          </div>
          <div
            className={`flex justify-between ${
              watchedState === 'TAKEN' ? '' : 'hidden'
            }`}
          >
            <Input
              label="quantity"
              type="number"
              placeholder="Quantity"
              step={0.01}
              {...form.register('quantity', {
                valueAsNumber: true,
                min: 0.01,
              })}
            />
            <div className="w-2/5">
              <Dropdown
                label="Unit"
                name="unit"
                placeholder="Unit"
                control={form.control}
                options={[
                  { value: 'MILLIGRAM', label: 'mg' },
                  { value: 'MILLILITER', label: 'mL' },
                ]}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <Button
            onClick={() => {
              form.reset();
              props.onClose();
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button loading={form.formState.isSubmitting} type="submit">
            Submit
          </Button>
        </div>
      </div>
    </form>
  );
}

function formatDoseUnit(du: DoseUnit) {
  switch (du) {
    case 'MILLIGRAM':
      return 'mg';
    case 'MILLILITER':
      return 'mL';
    default:
      return du;
  }
}

Medication.fragment = gql`
  fragment CustomerMedication on User {
    id
    dosageSchedules {
      id
      state
      schedule {
        id
        internalName
      }
      doses {
        id
        state
        dueAt
        dateTaken
        actualQuantity
        actualUnit
        scheduleDose: dose {
          id
          dose {
            id
            quantity
            unit
          }
        }
      }
    }
  }
`;
